import {
  listLegacyCommandCenterSurfaces,
  validateLegacyMigrationRegistry,
  type LegacyCommandCenterSurface,
} from './LegacyCommandCenterMigrationRegistry';

import {
  listSystemWorkspaces,
} from './SystemWorkspaceRegistry';

import {
  listSystemScenarioRegistrations,
} from './SystemScenarioRegistry';

import {
  listSystemAiAssistantRegistrations,
} from './SystemAiAssistantRegistry';

import {
  listSystemRepairRegistrations,
} from './SystemRepairRegistry';

export type LegacyRetirementDecision = {
  surfaceId: string;
  title: string;
  owningSystemId: string;
  eligible: boolean;
  blockers: string[];
};

export type GovernedWorkspaceCompletionRecord = {
  systemId: string;
  workspaceRegistered: boolean;
  legacySurfaceCount: number;
  mappedSurfaceCount: number;
  projectedSurfaceCount: number;
  certifiedSurfaceCount: number;
  retiredSurfaceCount: number;
  scenarioCount: number;
  aiAssistantCount: number;
  repairCount: number;
  completionState:
    | 'complete'
    | 'in_progress'
    | 'not_started';
  blockers: string[];
};

export type CommandCenterCompletionCertification = {
  certificationId: 'CC-SYS-001H';
  generatedAt: string;
  foundationStatus: 'PASS' | 'BLOCKED';
  migrationStatus:
    | 'COMPLETE'
    | 'IN_PROGRESS'
    | 'BLOCKED';
  nonDuplicationStatus: 'PASS' | 'BLOCKED';
  retirementStatus:
    | 'NONE_ELIGIBLE'
    | 'PARTIALLY_ELIGIBLE'
    | 'ALL_ELIGIBLE';
  registryValid: boolean;
  totalLegacySurfaces: number;
  registeredWorkspaceCount: number;
  governedOwnerCount: number;
  unregisteredOwningSystems: string[];
  duplicateSurfaceIds: string[];
  duplicateSourceReferences: string[];
  duplicateAuthorityGroups: string[];
  unmappedSurfaceIds: string[];
  retirementViolationIds: string[];
  retirementEligibleCount: number;
  retirementBlockedCount: number;
  completionPercentage: number;
  workspaceRecords:
    GovernedWorkspaceCompletionRecord[];
  retirementDecisions:
    LegacyRetirementDecision[];
  globalBlockers: string[];
};

function unique(
  values: string[],
): string[] {
  return [...new Set(values)].sort();
}

function duplicateValues(
  values: string[],
): string[] {
  const counts =
    new Map<string, number>();

  for (const value of values) {
    counts.set(
      value,
      (counts.get(value) ?? 0) + 1,
    );
  }

  return [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([value]) => value)
    .sort();
}

function retirementDecision(
  surface: LegacyCommandCenterSurface,
  registeredWorkspaceIds: Set<string>,
): LegacyRetirementDecision {
  const blockers: string[] = [];

  if (
    !registeredWorkspaceIds.has(
      surface.owningSystemId,
    )
  ) {
    blockers.push(
      'Owning governed workspace is not registered.',
    );
  }

  if (
    surface.migrationState !==
      'certified' &&
    surface.migrationState !==
      'retired'
  ) {
    blockers.push(
      'Governed replacement is not certified.',
    );
  }

  if (surface.legacyVisible) {
    blockers.push(
      'Legacy surface is still visible.',
    );
  }

  if (!surface.preserveEvidence) {
    blockers.push(
      'Evidence preservation is not certified.',
    );
  }

  if (!surface.preserveHistory) {
    blockers.push(
      'History preservation is not certified.',
    );
  }

  if (
    surface.duplicateAuthorityAllowed
  ) {
    blockers.push(
      'Duplicate authority remains allowed.',
    );
  }

  return {
    surfaceId: surface.id,
    title: surface.title,
    owningSystemId:
      surface.owningSystemId,
    eligible: blockers.length === 0,
    blockers,
  };
}

export function certifyCommandCenterCompletion(
  generatedAt =
    new Date().toISOString(),
): CommandCenterCompletionCertification {
  const surfaces =
    listLegacyCommandCenterSurfaces();

  const registryValidation =
    validateLegacyMigrationRegistry();

  const workspaces =
    listSystemWorkspaces();

  const registeredWorkspaceIds =
    new Set(
      workspaces.map(
        workspace => workspace.id,
      ),
    );

  const owningSystemIds =
    unique(
      surfaces.map(
        surface =>
          surface.owningSystemId,
      ),
    );

  const duplicateSourceReferences =
    duplicateValues(
      surfaces.map(
        surface =>
          surface.sourceReference,
      ),
    );

  const duplicateAuthorityGroups =
    duplicateValues(
      surfaces.map(
        surface =>
          `${surface.owningSystemId}:${surface.sourceReference}`,
      ),
    );

  const unregisteredOwningSystems =
    owningSystemIds.filter(
      systemId =>
        !registeredWorkspaceIds.has(
          systemId,
        ),
    );

  const retirementDecisions =
    surfaces.map(surface =>
      retirementDecision(
        surface,
        registeredWorkspaceIds,
      ),
    );

  const retirementEligibleCount =
    retirementDecisions.filter(
      decision => decision.eligible,
    ).length;

  const retirementBlockedCount =
    retirementDecisions.length -
    retirementEligibleCount;

  const workspaceRecords =
    owningSystemIds.map(
      (
        systemId,
      ): GovernedWorkspaceCompletionRecord => {
        const ownedSurfaces =
          surfaces.filter(
            surface =>
              surface.owningSystemId ===
              systemId,
          );

        const workspaceRegistered =
          registeredWorkspaceIds.has(
            systemId,
          );

        const mappedSurfaceCount =
          ownedSurfaces.filter(
            surface =>
              surface.migrationState !==
              'inventoried',
          ).length;

        const projectedSurfaceCount =
          ownedSurfaces.filter(
            surface =>
              surface.migrationState ===
                'projected' ||
              surface.migrationState ===
                'certified' ||
              surface.migrationState ===
                'retired',
          ).length;

        const certifiedSurfaceCount =
          ownedSurfaces.filter(
            surface =>
              surface.migrationState ===
                'certified' ||
              surface.migrationState ===
                'retired',
          ).length;

        const retiredSurfaceCount =
          ownedSurfaces.filter(
            surface =>
              surface.migrationState ===
              'retired',
          ).length;

        const blockers: string[] = [];

        if (!workspaceRegistered) {
          blockers.push(
            'Governed workspace is not registered.',
          );
        }

        if (
          mappedSurfaceCount !==
          ownedSurfaces.length
        ) {
          blockers.push(
            'One or more legacy surfaces remain unmapped.',
          );
        }

        if (
          certifiedSurfaceCount !==
          ownedSurfaces.length
        ) {
          blockers.push(
            'One or more governed replacement projections remain uncertified.',
          );
        }

        const completionState =
          blockers.length === 0
            ? 'complete'
            : workspaceRegistered
              ? 'in_progress'
              : 'not_started';

        return {
          systemId,
          workspaceRegistered,
          legacySurfaceCount:
            ownedSurfaces.length,
          mappedSurfaceCount,
          projectedSurfaceCount,
          certifiedSurfaceCount,
          retiredSurfaceCount,
          scenarioCount:
            listSystemScenarioRegistrations(
              systemId,
            ).length,
          aiAssistantCount:
            listSystemAiAssistantRegistrations(
              systemId,
            ).length,
          repairCount:
            listSystemRepairRegistrations(
              systemId,
            ).length,
          completionState,
          blockers,
        };
      },
    );

  const certifiedOrRetiredCount =
    surfaces.filter(
      surface =>
        surface.migrationState ===
          'certified' ||
        surface.migrationState ===
          'retired',
    ).length;

  const completionPercentage =
    surfaces.length === 0
      ? 0
      : Math.round(
          (
            certifiedOrRetiredCount /
            surfaces.length
          ) *
            100,
        );

  const globalBlockers: string[] = [];

  if (!registryValidation.valid) {
    globalBlockers.push(
      'Legacy migration registry validation failed.',
    );
  }

  if (
    unregisteredOwningSystems.length >
    0
  ) {
    globalBlockers.push(
      `${unregisteredOwningSystems.length} owning governed workspaces are not registered.`,
    );
  }

  if (
    duplicateSourceReferences.length >
    0
  ) {
    globalBlockers.push(
      'Duplicate legacy source references were detected.',
    );
  }

  if (
    duplicateAuthorityGroups.length >
    0
  ) {
    globalBlockers.push(
      'Duplicate authority groups were detected.',
    );
  }

  if (
    retirementEligibleCount === 0
  ) {
    globalBlockers.push(
      'No legacy surface is eligible for retirement.',
    );
  }

  if (
    certifiedOrRetiredCount !==
    surfaces.length
  ) {
    globalBlockers.push(
      'Governed replacement certification is incomplete.',
    );
  }

  const foundationStatus =
    registryValidation.valid &&
    duplicateSourceReferences.length ===
      0 &&
    duplicateAuthorityGroups.length ===
      0
      ? 'PASS'
      : 'BLOCKED';

  const migrationStatus =
    !registryValidation.valid
      ? 'BLOCKED'
      : certifiedOrRetiredCount ===
          surfaces.length &&
        unregisteredOwningSystems
          .length === 0
        ? 'COMPLETE'
        : 'IN_PROGRESS';

  const nonDuplicationStatus =
    registryValidation.duplicateIds
      .length === 0 &&
    duplicateSourceReferences.length ===
      0 &&
    duplicateAuthorityGroups.length ===
      0
      ? 'PASS'
      : 'BLOCKED';

  const retirementStatus =
    retirementEligibleCount === 0
      ? 'NONE_ELIGIBLE'
      : retirementEligibleCount ===
          retirementDecisions.length
        ? 'ALL_ELIGIBLE'
        : 'PARTIALLY_ELIGIBLE';

  return {
    certificationId: 'CC-SYS-001H',
    generatedAt,
    foundationStatus,
    migrationStatus,
    nonDuplicationStatus,
    retirementStatus,
    registryValid:
      registryValidation.valid,
    totalLegacySurfaces:
      surfaces.length,
    registeredWorkspaceCount:
      workspaces.length,
    governedOwnerCount:
      owningSystemIds.length,
    unregisteredOwningSystems,
    duplicateSurfaceIds:
      registryValidation.duplicateIds,
    duplicateSourceReferences,
    duplicateAuthorityGroups,
    unmappedSurfaceIds:
      registryValidation.unmappedIds,
    retirementViolationIds:
      registryValidation
        .retirementViolations,
    retirementEligibleCount,
    retirementBlockedCount,
    completionPercentage,
    workspaceRecords,
    retirementDecisions,
    globalBlockers,
  };
}
